import { setFieldValue, setFieldError } from "@/stores";

import { useAccountDetailsValidation } from "./useAccountDetailsValidation";
import { submitAccountDetails } from "../_helpers";
import { pushAnalyticsError, pushDataLayerEvent } from "@/helpers";

/**
 * useAccountDetailsForm Custom Hook
 *
 * Manages account details form state and submission logic.
 * Handles validation, API calls, error handling, and navigation.
 */
export const useAccountDetailsForm = () => {
  // Account details validation logic
  const { validateForm } = useAccountDetailsValidation();

  /**
   * Handles input change
   * Updates field value and clears any existing errors
   */
  const handleInputChange = (field: string, value: string) => {
    setFieldValue(field, value);
    setFieldError(field, "");
  };

  /**
   * Handles form submission
   * Validates form inputs, sets validation errors, and submits form data
   */
  const handleFormSubmit = async (values: any) => {
    // Push signup_start event for manual signup
    pushDataLayerEvent({
      event: "signup_start",
      method: "manual",
    });

    // Validate form first
    const validation = validateForm(values);

    // Set validation errors
    setFieldError("firstName", validation.firstNameError);
    setFieldError("lastName", validation.lastNameError);
    setFieldError("email", validation.emailError);
    setFieldError("termsAccepted", validation.termsAcceptedError);
    // setFieldError("phoneNumber", validation.phoneNumberError);

    // If validation fails, push error event and stop here
    if (validation.hasErrors) {
      const errorFields: string[] = [];
      const errorMessages: string[] = [];

      if (validation.firstNameError) {
        errorFields.push("first_name");
        errorMessages.push(validation.firstNameError);
      }
      if (validation.lastNameError) {
        errorFields.push("last_name");
        errorMessages.push(validation.lastNameError);
      }
      if (validation.emailError) {
        errorFields.push("email");
        errorMessages.push(validation.emailError);
      }
      if (validation.termsAcceptedError) {
        errorFields.push("terms_accepted");
        errorMessages.push(validation.termsAcceptedError);
      }
      // if (validation.phoneNumberError) {
      //   errorFields.push("phone");
      //   errorMessages.push(validation.phoneNumberError);
      // }

      pushAnalyticsError({
        event: "signup_error",
        errorCode: "validation",
        errorMessage: errorMessages.join("; ") || "Validation failed",
        errorStep: "account_details",
        method: "email",
      });

      return;
    }

    console.log(`Form values:`, values);

    // Submit form
    await submitAccountDetails(values);
  };

  return {
    handleInputChange,
    handleFormSubmit,
  };
};
